import React from "react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { routePaths } from "../constants/routes";
import "../style/jira-notes.css";
import jiraAgileHierarchyImage from "../assets/jira-agile-hierarchy.svg";
import jiraBugAnatomyImage from "../assets/jira-bug-anatomy.svg";
import jiraLifecycleImage from "../assets/jira-bug-lifecycle.svg";
import jiraBoardImage from "../assets/jira-board-roadmap.svg";
import jiraJqlImage from "../assets/jira-jql-dashboard.svg";
import jiraSeverityPriorityImage from "../assets/jira-severity-priority-matrix.svg";
import {
  jiraAgileBasics,
  jiraAdvancedJqlQueries,
  jiraBestInterviewAnswers,
  jiraBonusSections,
  jiraBugReportExample,
  jiraBugCreationTemplate,
  jiraBugLifeCycleStatuses,
  jiraDashboardReports,
  jiraDetailedQaAnswers,
  jiraDeliveryFormats,
  jiraEpicStoryExample,
  jiraFullTicketExample,
  jiraGlossaryTerms,
  jiraInterviewQuestionBank,
  jiraInterviewAnswers,
  jiraIntroHighlights,
  jiraIssueTypesMatrix,
  jiraModules,
  jiraMncInterviewQuestions,
  jiraNotesPackId,
  jiraPackIncludes,
  jiraQaWorkflowSteps,
  jiraJqlUseCases,
  jiraPracticeAssignments,
  jiraRealTimeBugExamples,
  jiraRealTimeExperience,
  jiraReadyMadeBugExamples,
  jiraRoadmapPhases,
  jiraSeverityPriorityExamples,
} from "../content/jiraNotesContent";
import { getPremiumAccess, hasPremiumAccess } from "../services/premiumNotesService";

const visualGuides = [
  {
    title: "Bug lifecycle visual",
    image: jiraLifecycleImage,
    alt: "JIRA bug lifecycle showing New, Assigned, In Progress, Fixed, Retest, Closed, and Reopened",
    text: "Use this when teaching defect movement from first report to final closure and reopen handling.",
  },
  {
    title: "Board and roadmap visual",
    image: jiraBoardImage,
    alt: "JIRA board visual showing backlog, sprint board, testing flow, and release roadmap",
    text: "Helps learners connect backlog items, sprint flow, QA execution, and release readiness in one view.",
  },
  {
    title: "JQL and dashboard visual",
    image: jiraJqlImage,
    alt: "JQL query and dashboard gadgets visual for JIRA learning",
    text: "Useful for understanding daily filters, saved dashboards, defect metrics, and management reporting.",
  },
  {
    title: "Agile hierarchy visual",
    image: jiraAgileHierarchyImage,
    alt: "JIRA Agile hierarchy showing Epic, Story, Task, Sub-task, Bug, and QA outcome",
    text: "Helps students understand how epic, story, task, sub-task, and bugs are connected in one QA flow.",
  },
  {
    title: "Severity vs priority matrix",
    image: jiraSeverityPriorityImage,
    alt: "Severity and priority matrix with real QA examples",
    text: "Makes interview concepts easy to remember using the four major severity-priority combinations.",
  },
  {
    title: "Bug report anatomy",
    image: jiraBugAnatomyImage,
    alt: "Visual explanation of a strong JIRA bug report structure",
    text: "Shows what students should include in a professional defect report: summary, steps, actual, expected, impact, and evidence.",
  },
];

function JiraNotes() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [purchaseMeta, setPurchaseMeta] = useState(null);
  const previewRows = 2;

  useEffect(() => {
    setIsUnlocked(hasPremiumAccess(jiraNotesPackId));
    setPurchaseMeta(getPremiumAccess(jiraNotesPackId));
  }, []);

  const visibleModules = isUnlocked ? jiraModules : jiraModules.slice(0, 3);
  const visibleInterviewAnswers = isUnlocked ? jiraInterviewAnswers : jiraInterviewAnswers.slice(0, 2);
  const visibleDetailedAnswers = isUnlocked ? jiraDetailedQaAnswers : jiraDetailedQaAnswers.slice(0, 3);
  const visibleReadyMadeBugs = isUnlocked ? jiraReadyMadeBugExamples : jiraReadyMadeBugExamples.slice(0, 1);
  const visibleBestAnswers = isUnlocked ? jiraBestInterviewAnswers : jiraBestInterviewAnswers.slice(0, 2);

  const renderSimpleTable = (headers, rows, locked = false) => (
    <div className="jira-module-card" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th
                key={header}
                style={{
                  textAlign: "left",
                  padding: "12px",
                  borderBottom: "1px solid rgba(148, 163, 184, 0.24)",
                  color: "#13213f",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(locked ? rows.slice(0, previewRows) : rows).map((row, index) => (
            <tr key={`${row[0]}-${index}`}>
              {row.map((cell, cellIndex) => (
                <td
                  key={`${cell}-${cellIndex}`}
                  style={{
                    padding: "12px",
                    verticalAlign: "top",
                    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
                    color: "#334155",
                    lineHeight: 1.7,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="jira-notes-page">
      <section className="jira-hero" id="overview">
        <div className="jira-hero-copy">
          <span className="jira-hero-eyebrow">Premium JIRA Notes Pack</span>
          <h1>JIRA for QA: detailed notes, roadmap, interview answers, and practical examples</h1>
          <p>
            A structured paid notes pack for learners who want JIRA explained clearly with QA-focused examples,
            workflow understanding, JQL practice, dashboards, reports, and real-time bug handling.
          </p>

          <div className="jira-price-panel">
            <div>
              <strong>Price</strong>
              <span>Rs. 99 only</span>
            </div>
            <div>
              <strong>Format</strong>
              <span>PDF-style notes + revision sections</span>
            </div>
            <div>
              <strong>Level</strong>
              <span>Beginner to advanced QA</span>
            </div>
          </div>

          {isUnlocked && purchaseMeta && (
            <div className="jira-unlocked-banner">
              <strong>Premium access active</strong>
              <span>{purchaseMeta.purchaseId} unlocked for this browser.</span>
            </div>
          )}

          <div className="jira-cta-row">
            <NavLink to={isUnlocked ? routePaths.jiraNotesDownload : routePaths.jiraNotesCheckout} className="jira-primary-cta">
              {isUnlocked ? "Download PDF Version" : "Buy Now Rs. 99"}
            </NavLink>
            {isUnlocked && (
              <NavLink to={routePaths.jiraNotesDownload} className="jira-secondary-cta">Open Premium Download View</NavLink>
            )}
            <NavLink to={routePaths.studyNotes} className="jira-secondary-cta">Back to Study Notes</NavLink>
          </div>
        </div>

        <div className="jira-hero-visual" aria-label="JIRA learning preview">
          <div className="jira-visual-card jira-visual-workflow">
            <span className="visual-label">Workflow Snapshot</span>
            <p>Open -> Assigned -> In Progress -> Retest -> Closed</p>
          </div>
          <div className="jira-visual-card jira-visual-jql">
            <span className="visual-label">JQL Snapshot</span>
            <code>project = QA AND status = "Open"</code>
          </div>
          <div className="jira-visual-card jira-visual-roadmap">
            <span className="visual-label">Roadmap Snapshot</span>
            <p>Foundations -> Agile -> JQL -> Reporting -> Interviews</p>
          </div>
        </div>
      </section>

      <section className="jira-section" id="includes">
        <div className="jira-section-heading">
          <span>What learners get</span>
          <h2>Built as a compact paid pack, not just a simple topic list</h2>
        </div>

        <div className="jira-info-grid">
          <article className="jira-info-card">
            <h3>Pack includes</h3>
            <ul>
              {jiraPackIncludes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="jira-info-card">
            <h3>Formats you can offer</h3>
            <div className="jira-delivery-list">
              {jiraDeliveryFormats.map((item) => (
                <div key={item.title} className="jira-delivery-card">
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="jira-section" id="premium-structure">
        <div className="jira-section-heading">
          <span>Premium structure</span>
          <h2>Chapter-wise PDF preview that feels like a serious paid product</h2>
        </div>

        <div className="jira-info-grid">
          {renderSimpleTable(["Topic", "Details"], jiraIntroHighlights, !isUnlocked)}
          {renderSimpleTable(["Agile Term", "Meaning"], jiraAgileBasics, !isUnlocked)}
        </div>

        <div className={`jira-bug-example ${!isUnlocked ? "jira-section-locked" : ""}`.trim()} style={{ marginTop: "20px" }}>
          {renderSimpleTable(["Epic", "Story", "Task", "Bug"], jiraEpicStoryExample, !isUnlocked)}
          {renderSimpleTable(["Issue Type", "Meaning", "QA Example"], jiraIssueTypesMatrix, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Only a small chapter preview is visible. Full issue types and examples unlock after payment.</p>}
      </section>

      <section className="jira-section" id="roadmap">
        <div className="jira-section-heading">
          <span>Roadmap</span>
          <h2>Simple learning path for a fresher, tester, or job-seeker</h2>
        </div>

        <div className="jira-roadmap-grid">
          {(isUnlocked ? jiraRoadmapPhases : jiraRoadmapPhases.slice(0, 2)).map((phase) => (
            <article className="jira-roadmap-card" key={phase.title}>
              <div className="jira-roadmap-top">
                <span>{phase.duration}</span>
                <h3>{phase.title}</h3>
              </div>
              <ul>
                {phase.topics.map((topic) => (
                  <li key={topic}>{topic}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Roadmap preview is limited to attract users. The full phase-by-phase learning plan is premium.</p>}
      </section>

      <section className="jira-section" id="bug-lifecycle">
        <div className="jira-section-heading">
          <span>Bug life cycle</span>
          <h2>One of the strongest selling sections for QA learners and job seekers</h2>
        </div>

        <div className={`jira-bug-example ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          {renderSimpleTable(["Status", "Meaning"], jiraBugLifeCycleStatuses, !isUnlocked)}
          <article className="jira-bug-card">
            <h3>Lifecycle flow</h3>
            <p>Open -> Assigned -> In Progress -> Fixed -> Ready for QA -> Retest -> Closed</p>
            <p className="jira-highlight-note">If the issue still exists after retest, it moves back to Reopened.</p>
          </article>
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Only the first few lifecycle states are visible in preview mode.</p>}
      </section>

      <section className="jira-section" id="images">
        <div className="jira-section-heading">
          <span>Visual learning</span>
          <h2>Images and diagrams for faster understanding</h2>
        </div>

        <div className="jira-visual-guide-grid">
          {visualGuides.map((item) => (
            <article className="jira-visual-guide-card" key={item.title}>
              <img src={item.image} alt={item.alt} />
              <div className="jira-visual-guide-copy">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="jira-section" id="real-time-user-experience">
        <div className="jira-section-heading">
          <span>Real-time experience</span>
          <h2>How JIRA feels in an actual QA workday</h2>
        </div>

        <div className="jira-experience-grid">
          {(isUnlocked ? jiraRealTimeExperience : jiraRealTimeExperience.slice(0, 2)).map((item) => (
            <article className="jira-experience-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Real project execution scenarios are partially hidden until purchase.</p>}
      </section>

      <section className="jira-section" id="syllabus">
        <div className="jira-section-heading">
          <span>Detailed syllabus</span>
          <h2>Clear answers for the most important JIRA topics</h2>
        </div>

        <div className="jira-module-list">
          {visibleModules.map((module) => (
            <article className="jira-module-card" key={module.title}>
              <h3>{module.title}</h3>
              <p>{module.answer}</p>
              <div className="jira-tag-list">
                {module.points.map((point) => (
                  <span key={point}>{point}</span>
                ))}
              </div>
            </article>
          ))}
        </div>

        {!isUnlocked && (
          <div className="jira-lock-overlay-card">
            <span>Premium preview</span>
            <h3>More modules unlock after purchase</h3>
            <p>Get the full advanced sections, extra workflow details, enterprise interview prep, complete JQL blocks, and PDF download access for Rs. 99.</p>
            <NavLink to={routePaths.jiraNotesCheckout} className="jira-primary-cta">Unlock Full Pack</NavLink>
          </div>
        )}
      </section>

      <section className="jira-section" id="bug-example">
        <div className="jira-section-heading">
          <span>Practical example</span>
          <h2>What a strong real-time JIRA bug should look like</h2>
        </div>

        <div className={`jira-bug-example ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          <article className="jira-bug-card">
            <h3>{jiraBugReportExample.title}</h3>
            <ul>
              {jiraBugReportExample.fields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </article>

          <article className="jira-bug-card">
            <h3>Steps to reproduce</h3>
            <ol>
              {jiraBugReportExample.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p><strong>Expected:</strong> {jiraBugReportExample.expected}</p>
            <p><strong>Actual:</strong> {jiraBugReportExample.actual}</p>
            <p className="jira-highlight-note">{jiraBugReportExample.qaNote}</p>
          </article>
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Bug example details are preview-only here. Unlock to use the full premium study and PDF version.</p>}
      </section>

      <section className="jira-section" id="bug-template">
        <div className="jira-section-heading">
          <span>Bug template</span>
          <h2>Real bug creation structure for paid learners</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Field", "Example"], jiraBugCreationTemplate, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Only a small bug template preview is shown to unpaid users.</p>}
      </section>

      <section className="jira-section" id="real-time-bugs">
        <div className="jira-section-heading">
          <span>Real-time bug examples</span>
          <h2>Examples learners can directly use in practice and interviews</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Application", "Bug Scenario", "Severity", "Priority"], jiraRealTimeBugExamples, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Most real-time bug examples are locked to protect premium content.</p>}
      </section>

      <section className="jira-section" id="severity-priority">
        <div className="jira-section-heading">
          <span>Severity vs priority</span>
          <h2>Critical concept explained with business-style examples</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Case", "Meaning", "Example"], jiraSeverityPriorityExamples, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Severity vs priority examples are preview-limited until payment.</p>}

        <div className="jira-visual-focus-card">
          <img src={jiraSeverityPriorityImage} alt="Severity and priority matrix for JIRA QA students" />
          <div className="jira-visual-focus-copy">
            <h3>Remember this matrix for interviews</h3>
            <p>
              Students usually confuse severity and priority. This visual shows all four combinations with practical
              scenarios, so they can explain answers clearly in interviews and while reporting defects in projects.
            </p>
          </div>
        </div>
      </section>

      <section className="jira-section" id="qa-workflow">
        <div className="jira-section-heading">
          <span>QA workflow</span>
          <h2>End-to-end JIRA flow for daily project execution</h2>
        </div>

        <article className={`jira-module-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          <ol style={{ margin: 0, paddingLeft: "20px", color: "#334155", lineHeight: 1.9 }}>
            {(isUnlocked ? jiraQaWorkflowSteps : jiraQaWorkflowSteps.slice(0, 4)).map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </article>
        {!isUnlocked && <p className="jira-lock-caption">Only the first few workflow steps are visible in free preview.</p>}
      </section>

      <section className="jira-section" id="reports">
        <div className="jira-section-heading">
          <span>Dashboards and reports</span>
          <h2>Advanced visibility topics that increase the product value</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Report", "Use"], jiraDashboardReports, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Dashboard and reporting coverage is premium-only beyond teaser rows.</p>}
      </section>

      <section className="jira-section" id="advanced-jql">
        <div className="jira-section-heading">
          <span>Advanced practice</span>
          <h2>Useful JQL queries for daily QA and lead-level tracking</h2>
        </div>

        <div className={`jira-query-grid ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          {(isUnlocked ? jiraAdvancedJqlQueries : jiraAdvancedJqlQueries.slice(0, 2)).map((query) => (
            <article className="jira-query-card" key={query}>
              <code>{query}</code>
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Full advanced JQL set unlocks after purchase.</p>}
      </section>

      <section className="jira-section" id="jql-use-cases">
        <div className="jira-section-heading">
          <span>JQL use cases</span>
          <h2>High-value query examples for fresher to advanced QA</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Requirement", "JQL Query"], jiraJqlUseCases, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Only a couple of JQL use-cases are visible before payment.</p>}
      </section>

      <section className="jira-section" id="interview-questions">
        <div className="jira-section-heading">
          <span>Interview support</span>
          <h2>Short, direct answers learners can revise quickly</h2>
        </div>

        <div className="jira-qa-grid">
          {visibleInterviewAnswers.map((item) => (
            <article className="jira-qa-card" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="jira-section" id="detailed-qa-answers">
        <div className="jira-section-heading">
          <span>Detailed Q&A chapter</span>
          <h2>Structured JIRA questions and answers for QA interviews and paid study material</h2>
        </div>

        <div className="jira-module-list">
          {visibleDetailedAnswers.map((item) => (
            <article className={`jira-module-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()} key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
              {item.table && renderSimpleTable(item.table.headers, item.table.rows, !isUnlocked)}
              {item.extraTable && (
                <div style={{ marginTop: "16px" }}>
                  {renderSimpleTable(item.extraTable.headers, item.extraTable.rows, !isUnlocked)}
                </div>
              )}
              {item.bullets && (
                <ul style={{ paddingLeft: "20px", color: "#334155", lineHeight: 1.8 }}>
                  {(isUnlocked ? item.bullets : item.bullets.slice(0, 2)).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
              {item.example && <p className="jira-highlight-note">{item.example}</p>}
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Only a few detailed questions are visible in preview. Full question bank unlocks after payment.</p>}
      </section>

      <section className="jira-section" id="interview-bank">
        <div className="jira-section-heading">
          <span>Interview bank</span>
          <h2>Question bank preview for the paid PDF</h2>
        </div>

        <article className={`jira-module-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          <ul style={{ margin: 0, paddingLeft: "20px", color: "#334155", lineHeight: 1.9 }}>
            {(isUnlocked ? jiraInterviewQuestionBank : jiraInterviewQuestionBank.slice(0, 4)).map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </article>
        {!isUnlocked && <p className="jira-lock-caption">Unlock the full interview bank and premium answers after purchase.</p>}
      </section>

      <section className="jira-section" id="mnc-interview-questions">
        <div className="jira-section-heading">
          <span>MNC interview prep</span>
          <h2>Enterprise-level questions with stronger answers</h2>
        </div>

        <div className={`jira-mnc-grid ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          {(isUnlocked ? jiraMncInterviewQuestions : jiraMncInterviewQuestions.slice(0, 1)).map((item) => (
            <article className="jira-mnc-card" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Premium unlock includes the full MNC interview bank and stronger scenario answers.</p>}
      </section>

      <section className="jira-section" id="ready-made-bugs">
        <div className="jira-section-heading">
          <span>Ready-made bug examples</span>
          <h2>Sellable practical examples students can directly revise before interviews</h2>
        </div>

        <div className="jira-qa-grid">
          {visibleReadyMadeBugs.map((item) => (
            <article className={`jira-qa-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()} key={item.title}>
              <h3>{item.title}</h3>
              <p><strong>Summary:</strong> {item.summary}</p>
              <ol style={{ paddingLeft: "20px", color: "#334155", lineHeight: 1.8 }}>
                {(isUnlocked ? item.steps : item.steps.slice(0, 3)).map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p><strong>Actual:</strong> {item.actual}</p>
              <p><strong>Expected:</strong> {item.expected}</p>
              <p><strong>Severity:</strong> {item.severity} | <strong>Priority:</strong> {item.priority}</p>
              <p className="jira-highlight-note"><strong>Root Cause:</strong> {item.rootCause}</p>
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Most ready-made bug examples are locked to keep the paid PDF valuable.</p>}

        <div className="jira-visual-focus-card">
          <img src={jiraBugAnatomyImage} alt="Anatomy of a strong JIRA bug report" />
          <div className="jira-visual-focus-copy">
            <h3>Bug report structure made visual</h3>
            <p>
              Instead of only reading field names, students can now see how a complete bug is structured in one
              screen. This is useful for freshers who struggle to write strong summaries, steps, and actual vs
              expected results.
            </p>
          </div>
        </div>
      </section>

      <section className="jira-section" id="best-answers">
        <div className="jira-section-heading">
          <span>Best interview answers</span>
          <h2>Strong spoken answers for interviews, demos, and trainer sessions</h2>
        </div>

        <div className="jira-qa-grid">
          {visibleBestAnswers.map((item) => (
            <article className={`jira-qa-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()} key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="jira-section" id="assignments">
        <div className="jira-section-heading">
          <span>Practice assignments</span>
          <h2>Mini tasks that make the premium notes feel like a course workbook</h2>
        </div>

        <div className="jira-module-list">
          {(isUnlocked ? jiraPracticeAssignments : jiraPracticeAssignments.slice(0, 1)).map((item) => (
            <article className={`jira-module-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()} key={item.title}>
              <h3>{item.title}</h3>
              {item.points && (
                <ul style={{ paddingLeft: "20px", color: "#334155", lineHeight: 1.8 }}>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
              {item.table && renderSimpleTable(item.table.headers, item.table.rows, !isUnlocked)}
            </article>
          ))}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Assignments and answer grids are part of the premium learning workbook.</p>}
      </section>

      <section className="jira-section" id="full-ticket">
        <div className="jira-section-heading">
          <span>Complete JIRA ticket</span>
          <h2>One full real-time ticket to show students exactly how a strong defect looks</h2>
        </div>

        <article className={`jira-module-card ${!isUnlocked ? "jira-section-locked" : ""}`.trim()}>
          <h3>{jiraFullTicketExample.summary}</h3>
          <p><strong>Project:</strong> {jiraFullTicketExample.project}</p>
          <p><strong>Issue Type:</strong> {jiraFullTicketExample.issueType}</p>
          <p>{jiraFullTicketExample.description}</p>
          <ul style={{ paddingLeft: "20px", color: "#334155", lineHeight: 1.8 }}>
            {(isUnlocked ? jiraFullTicketExample.preconditions : jiraFullTicketExample.preconditions.slice(0, 2)).map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <ol style={{ paddingLeft: "20px", color: "#334155", lineHeight: 1.8 }}>
            {(isUnlocked ? jiraFullTicketExample.steps : jiraFullTicketExample.steps.slice(0, 4)).map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          <p><strong>Actual:</strong> {jiraFullTicketExample.actual}</p>
          <p><strong>Expected:</strong> {jiraFullTicketExample.expected}</p>
          <p><strong>Severity:</strong> {jiraFullTicketExample.severity} | <strong>Priority:</strong> {jiraFullTicketExample.priority}</p>
          {isUnlocked && (
            <>
              <p><strong>Environment:</strong> {jiraFullTicketExample.environment}</p>
              <p><strong>Test Data:</strong> {jiraFullTicketExample.testData}</p>
              <p><strong>Attachment:</strong> {jiraFullTicketExample.attachment}</p>
              <p><strong>Root Cause:</strong> {jiraFullTicketExample.rootCause}</p>
              <p><strong>Developer Comment:</strong> {jiraFullTicketExample.developerComment}</p>
              <p><strong>QA Retest Comment:</strong> {jiraFullTicketExample.qaRetestComment}</p>
              <p><strong>Status:</strong> {jiraFullTicketExample.status}</p>
            </>
          )}
        </article>
        {!isUnlocked && <p className="jira-lock-caption">The full ticket with RCA, comments, and retest proof is available only after purchase.</p>}
      </section>

      <section className="jira-section" id="glossary">
        <div className="jira-section-heading">
          <span>Glossary</span>
          <h2>Important JIRA terms for final revision and quick PDF value</h2>
        </div>

        <div className={!isUnlocked ? "jira-section-locked" : ""}>
          {renderSimpleTable(["Term", "Meaning"], jiraGlossaryTerms, !isUnlocked)}
        </div>
        {!isUnlocked && <p className="jira-lock-caption">Glossary preview is limited to keep the revision chapter premium.</p>}
      </section>

      <section className="jira-section" id="bonus">
        <div className="jira-section-heading">
          <span>Bonus sections</span>
          <h2>Extra value you can keep inside the paid notes pack</h2>
        </div>

        <div className="jira-bonus-panel">
          <ul>
            {jiraBonusSections.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className="jira-bonus-callout">
            <h3>Recommended offer position</h3>
            <p>
              Present this as a premium JIRA notes pack for Rs. 99 with chapter-wise content, roadmap,
              practical examples, and interview revision material. This keeps it affordable while still feeling
              like a structured mini-product instead of a plain article.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default JiraNotes;
